/**
 * **TASK SUPPORT 에서 감춘 붙박이** — 워크스페이스 문서의 `menuTasks` 한 필드.
 *
 * 🚨 **기본은 «다 켜짐»이고, 끈 것만 적는다**(내 화면의 감추기와 같은 규약) — 켠 것을 적는
 *   방식이면 새 붙박이가 들어올 때마다 아무에게도 안 보인다.
 * 🔑 **지우는 것이 아니라 감추는 것이다** — 화면과 라우트는 그대로다. 다시 켜면 제자리에 선다.
 * ⚠️ **모르는 열쇠는 떨군다** — 사라진 화면의 열쇠가 남아 있으면 그 줄만 영영 안 보인다.
 */

/**
 * TASK SUPPORT 에 서는 붙박이 — 차례가 곧 메뉴 차례다(`nav-config-service`).
 *
 * 🚨 **「AI 작업」은 여기 없다** — 그 화면은 「새 작업 만들기」이고, 들어가는 길은 작업 관리의
 *   단추다. 만드는 자리를 메뉴에 또 세우면 「작업이 어디 있나」에 답이 둘이 된다.
 */
export const TASK_SUPPORT_KEYS = [
  'influencerCheck',
  'campaign',
  'idea',
  'externalDashboard',
] as const;

export type TaskSupportKey = (typeof TASK_SUPPORT_KEYS)[number];

/**
 * 붙박이의 **한 줄 설명** — 작업 관리 카드가 읽는다.
 * 🔑 이름·아이콘·관문은 사이드바가 쥔 것을 그대로 쓰고(`getNavData`), **설명만** 여기 있다 —
 *   메뉴에는 설명이 없어서다. 두 곳에 같은 것을 적지 않는다.
 */
export const TASK_SUPPORT_HINTS: Record<string, string> = {
  influencerCheck: '섭외 전에 그 계정이 진짜인지, 어떤 글을 올리는지 본다',
  campaign: '집행 중인 시딩이 실제로 올라왔는지, 얼마나 닿았는지 본다',
  idea: '지금 반응이 좋은 이야기에서 다음에 만들 것을 뽑는다',
  externalDashboard: '밖에 내보일 지면을 만들고 링크로 연다',
};

const KNOWN = new Set<string>(TASK_SUPPORT_KEYS);

/** 문서에 적힌 것을 읽는다 — 모르는 열쇠는 떨군다(머리말) */
export function parseHiddenTasks(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(
    value
      .map((row) => (typeof row === 'string' ? row.trim() : ''))
      .filter((row) => KNOWN.has(row)),
  )];
}

/** 켜고 끈다 */
export function toggleHiddenTask(rows: readonly string[], key: string): string[] {
  if (!KNOWN.has(key)) return [...rows];
  return rows.includes(key) ? rows.filter((row) => row !== key) : [...rows, key];
}

/**
 * 메뉴에 세울 붙박이만 남긴다.
 * ⚠️ **마지막 하나는 못 끈다** — 구역이 통째로 사라지면 다시 켤 고르개도 함께 사라진다
 *   (고르개가 그 구역 머리에 달려 있다).
 */
export function visibleTaskKeys(keys: readonly string[], hidden: readonly string[]): string[] {
  const shown = keys.filter((key) => !hidden.includes(key));
  return shown.length > 0 ? shown : [...keys].slice(0, 1);
}
